import SiteCoreLinks from "@/components/SiteCoreLinks";
// Phase NOTARY-FOCUS N3 — /notary/coverage (TH) — 77-province nationwide coverage
import { createFileRoute, Link } from "@tanstack/react-router";
import { SnippetAnswer } from "@/components/seo/SnippetAnswer";
import { hreflangLinks, SEO_SITE, ctaDesc, ctaTitle } from "@/lib/seo";
import { areaServedSchema, NOTARY_PROVINCE_COUNT } from "@/data/notary-coverage";
import NotaryCoverageTable from "@/components/NotaryCoverageTable";
import RelatedNotaryLinks from "@/components/RelatedNotaryLinks";

import { ShieldCheck, MessageCircle, Phone, Truck } from "lucide-react";

const URL = `${SEO_SITE}/notary/coverage`;
const TITLE = "พื้นที่ให้บริการ Notary Public — ครอบคลุม 77 จังหวัดทั่วไทย | NYC";
const DESC = "บริการ Notary Public ครอบคลุม 77 จังหวัดทั่วไทย ผ่านสาขา 4 แห่ง (กทม./ขอนแก่น/อุดร/หนองคาย) + สำนักงานตัวแทน 8 จังหวัด + Remote Signing — แจ้งเอกสารที่มี รู้ขั้นตอนและระยะเวลาใน 10 นาที";

export const Route = createFileRoute("/notary/coverage")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/notary/coverage", "th")}, { name: "description", content: ctaDesc(DESC, "/notary/coverage", "th")},
      { property: "og:title", content: ctaTitle(TITLE, "/notary/coverage", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/notary/coverage", "th")},
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks("/notary/coverage")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org", "@type": "LegalService",
          name: "NYC Legal — Notary Public Coverage Thailand",
          url: URL,
          areaServed: areaServedSchema(),
          serviceType: "Notarial Services Attorney",
          priceRange: "฿500–฿5,000",
          provider: { "@type": "LegalService", name: "NYC Language Institute", url: SEO_SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "NYC ให้บริการ Notary Public ครอบคลุมกี่จังหวัด?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "NYC Language Institute ให้บริการ Notary Public ครอบคลุม 77 จังหวัดทั่วประเทศไทย ผ่านสาขา 4 แห่ง (กรุงเทพฯ ลาดพร้าว 95, ขอนแก่น, อุดรธานี, หนองคาย) สำนักงานตัวแทน 8 จังหวัด (เชียงใหม่, เชียงราย, อยุธยา, ชลบุรี, ระยอง, โคราช, อุบลฯ, สุราษฎร์ฯ, ภูเก็ต, หาดใหญ่) และ Remote Signing + EMS รับ-ส่งเอกสารทุกจังหวัดภายใน 1–3 วันทำการ",
              },
            },
            {
              "@type": "Question",
              name: "Remote Signing Notary ทำงานอย่างไร?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "หากท่านอยู่ต่างจังหวัด ทีม NYC ตรวจเอกสารผ่าน LINE/อีเมล นัดวันส่ง EMS ต้นฉบับเข้า กทม. ทนาย Notarial Services Attorney ลงนามรับรองในวันรับเอกสาร และจัดส่งกลับ EMS ภายใน 24 ชั่วโมง รวมเวลาทั้งหมด 2–3 วันทำการ ค่าบริการเริ่ม ฿500 ค่าจัดส่ง ฿80",
              },
            },
            {
              "@type": "Question",
              name: "พื้นที่ Pattaya / Phuket / Chiang Mai มี Notary นัดในเมืองไหม?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "มี — NYC มีสำนักงานตัวแทน Notarial Attorney ในเชียงใหม่ พัทยา (ชลบุรี) ภูเก็ต และหาดใหญ่ (สงขลา) นัดเซ็นเอกสารต่อหน้าทนายในตัวเมืองได้ภายใน 1 วันทำการ ค่าบริการเริ่ม ฿800 รวมค่าเดินทางทนาย",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: NotaryCoveragePage,
});

function NotaryCoveragePage() {
  return (
    <main className="min-h-screen bg-white">
      <section className="relative bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
          <nav aria-label="Breadcrumb" className="mb-4 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/notary" className="hover:text-gold">Notary Public</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">พื้นที่ให้บริการ</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> 77 จังหวัดทั่วไทย · Notarial Services Attorney
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            พื้นที่ให้บริการ Notary Public<br className="hidden sm:block" />
            <span className="text-gold">ครอบคลุม 77 จังหวัดทั่วประเทศไทย</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            ไม่ว่าคุณจะอยู่ที่ไหนในประเทศไทย — กรุงเทพฯ เชียงใหม่ ภูเก็ต พัทยา หรือชายแดน — NYC Legal มีทีม Notarial Services Attorney พร้อมรับรองเอกสารของคุณภายใน 1–3 วันทำการ ผ่านสาขา 4 แห่ง สำนักงานตัวแทน 10+ จังหวัด และบริการ Remote Signing + EMS
          </p>
          <div className="mt-6 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> ส่งเอกสารทาง LINE
            </a>
            <a href="tel:0832494999"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        <SnippetAnswer
          question="NYC ให้บริการ Notary Public ครอบคลุมพื้นที่ใดในประเทศไทย?"
          answer="NYC Legal ให้บริการ Notary Public ครอบคลุม 77 จังหวัดทั่วประเทศไทย ผ่านสาขาประจำ 4 แห่ง (กรุงเทพฯ ลาดพร้าว 95, ขอนแก่น, อุดรธานี, หนองคาย) สำนักงานตัวแทน 10 จังหวัด และบริการ Remote Signing + EMS รับ-ส่งเอกสารทุกจังหวัดภายใน 1–3 วันทำการ ค่าบริการเริ่มต้น 500 บาทต่อเอกสาร"
          steps={{
            type: "ordered",
            items: [
              "ส่งสำเนาเอกสาร + บัตรประจำตัวทาง LINE @nycli หรืออีเมล",
              "ทีมตรวจสอบและยืนยันราคา + เวลานัด ภายใน 30 นาที",
              "ส่งต้นฉบับเข้า กทม. ทาง EMS (หรือเดินทางมาเซ็นที่สำนักงานตัวแทน)",
              "Notarial Services Attorney รับรองเอกสารวันเดียวกัน",
              "จัดส่งกลับ EMS / Kerry / Lalamove ภายใน 24 ชั่วโมง",
            ],
          }}
          lang="th"
        />
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <h2 className="mb-6 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
          77 จังหวัดทั่วไทย · {NOTARY_PROVINCE_COUNT} จังหวัด
        </h2>
        <NotaryCoverageTable lang="th" />
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
          <div className="flex items-start gap-3">
            <Truck className="mt-1 h-6 w-6 text-amber-600" />
            <div>
              <h3 className="font-display text-lg font-extrabold text-navy-deep">
                Remote Signing + EMS — สำหรับลูกค้าทุกจังหวัด
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                หากท่านไม่สะดวกเดินทางเข้า กทม. ทีม NYC จัดบริการรับ-ส่งเอกสารถึงบ้านทั่วประเทศไทย
                ค่าบริการ Notary เริ่ม ฿1,500 + ค่าจัดส่ง EMS/Kerry ฿80–฿150
                ระยะเวลารวม 2–3 วันทำการ พร้อมต่อ Apostille หรือสถานทูตทันที
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/notary" className="rounded-full bg-navy-deep px-4 py-2 text-sm font-bold text-white hover:bg-navy-deep/90">
                  ดูบริการ Notary ทั้งหมด
                </Link>
                <Link to="/apostille" className="rounded-full bg-white px-4 py-2 text-sm font-bold text-navy-deep ring-1 ring-slate-300 hover:bg-slate-50">
                  ต่อ Apostille
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <RelatedNotaryLinks context="coverage" lang="th" />
          <SiteCoreLinks />
    </main>
  );
}

